import { useTranslation } from 'react-i18next';
import type { TreeMenuItem, ProcessType } from '@/mocks/customizationMenu';
import { PROCESS_TYPE_ICONS } from '@/mocks/customizationMenu';

interface CustomizationTreeProps {
  items: TreeMenuItem[];
  onAddProcess: (id: string, labelKey: string, processType: ProcessType) => void;
  disabled?: boolean;
}

export default function CustomizationTree({ items, onAddProcess, disabled = false }: CustomizationTreeProps) {
  const { t } = useTranslation('customize');

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2.5 border-b border-ink-100 bg-cream-50/50 shrink-0">
        <h3 className="text-xs font-bold text-ink-500 uppercase tracking-wider flex items-center gap-1.5">
          <i className="ri-git-branch-line text-warm-500 text-sm" />
          {t('customize.tree.title')}
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {disabled && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-warm-50 border border-warm-200 mb-1">
            <i className="ri-lock-line text-warm-500 text-sm shrink-0" />
            <span className="text-[11px] font-bold text-warm-600 leading-snug">
              {t('customize.tree.loginRequired')}
            </span>
          </div>
        )}
        {items.map((item) => {
          const icon = item.processType
            ? PROCESS_TYPE_ICONS[item.processType]
            : item.icon;
          const label = item.labelKey ? t(item.labelKey) : item.label;
          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.processType) {
                  onAddProcess(item.id, item.labelKey || item.label, item.processType);
                }
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-3 rounded-xl hover:bg-warm-50 border border-transparent hover:border-warm-200 transition-all duration-200 cursor-pointer text-left group ${
                disabled ? 'opacity-60' : ''
              }`}
            >
              <span className="w-8 h-8 rounded-lg bg-ink-50 group-hover:bg-warm-100 flex items-center justify-center shrink-0 transition-colors">
                <i className={`${icon} text-ink-400 group-hover:text-warm-500 text-sm transition-colors`} />
              </span>
              <span className="text-xs font-bold text-ink-600 group-hover:text-warm-600 flex-1 transition-colors leading-snug">
                {label}
              </span>
              {disabled ? (
                <span className="w-6 h-6 rounded-full bg-ink-50 flex items-center justify-center shrink-0">
                  <i className="ri-lock-line text-ink-300 text-xs" />
                </span>
              ) : (
                <span className="w-6 h-6 rounded-full bg-ink-50 group-hover:bg-warm-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shrink-0">
                  <i className="ri-add-line text-warm-500 text-xs" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}